/**
 * QUAL-001 / D143 — les bornes de « Constatée le », côté ÉCRAN.
 *
 * Le serveur refuse une date de constat postérieure au jour de l'ORGANISME
 * (`NC_DETECTED_IN_FUTURE`), ou postérieure à « Clôturée le » d'une fiche close
 * (`NC_DETECTED_AFTER_CLOSURE`) : 422 à code, la borne NOMMÉE dans `limit`
 * (`backend/src/infra/quality_repository/_nc_constat.py`). Sans ces bornes, le
 * « Temps de résolution » (`closed_at - detected_at`) tombait en négatif.
 *
 * Ici : la borne du sélecteur (la même que le serveur, pour ne rien offrir
 * qu'il refuse), et la phrase du refus dans la langue du lecteur, redite SOUS
 * le champ (moule D30/D46). La phrase de l'API, en français, ne s'affiche pas
 * telle quelle.
 */
import type { TFunction } from 'i18next'

import { ApiError } from '@/lib/api-error'
import { formatCalendarDate, fromISODate } from '@/lib/date-utils'

export const REFUS_DU_CONSTAT = ['NC_DETECTED_IN_FUTURE', 'NC_DETECTED_AFTER_CLOSURE'] as const
export type RefusDuConstat = (typeof REFUS_DU_CONSTAT)[number]

export function estUnRefusDuConstat(code: unknown): code is RefusDuConstat {
    return REFUS_DU_CONSTAT.some((c) => c === code)
}

/**
 * La phrase d'un refus du constat, ou `null` si `code` n'en est pas un.
 * `limite` : la date qui borne (ISO) — celle du serveur quand il l'a servie.
 */
export function phraseDuRefusDuConstat(
    code: unknown,
    limite: string | null | undefined,
    t: TFunction,
    langue: string,
): string | null {
    if (!estUnRefusDuConstat(code)) return null
    return t(`quality.nc.detectedAtRefused.${code}`, {
        date: limite ? formatCalendarDate(limite, langue) : '—',
    })
}

/** Le refus porté par une erreur d'API (création) : son code et sa borne `limit`. */
export function phraseDuRefusDuConstatApi(err: unknown, t: TFunction, langue: string): string | null {
    if (!(err instanceof ApiError)) return null
    const limite = typeof err.details?.limit === 'string' ? err.details.limit : null
    return phraseDuRefusDuConstat(err.code, limite, t, langue)
}

/**
 * La borne HAUTE du sélecteur : le jour de l'organisme, ou « Clôturée le » s'il
 * est antérieur (fiche close). `clotureeLe` : `closed_at` (ISO) ou rien.
 */
export function borneDuConstat(borneHaute: Date, clotureeLe: string | null | undefined): Date {
    const cloture = clotureeLe ? fromISODate(clotureeLe) : null
    return cloture && cloture < borneHaute ? cloture : borneHaute
}
